import { useEffect, useMemo, useState } from "react";
import { paymentsBreakdown } from "@/lib/api";
import { EUR, num, paymentMethodIT } from "@/lib/format";

/**
 * Compact "Pagamenti per metodo" card.
 *
 * Operational tracking only — supports the monthly Cassa-vs-Banca check.
 * No accounting logic, no ledger structure: it just aggregates the payments
 * registered in the selected window by their `method` field.
 *
 * Props:
 *   - dateFrom / dateTo : ISO yyyy-mm-dd window
 *   - title             : optional header label (default: "Pagamenti per metodo")
 *   - subtitle          : optional explanatory line
 *   - categoryId        : optional category filter passed to the backend
 *   - vendorId          : optional vendor filter passed to the backend
 *   - testId            : data-testid root, default "payment-method-breakdown"
 *   - compact           : visual density, smaller paddings
 */
export function PaymentMethodBreakdown({
  dateFrom,
  dateTo,
  title = "Pagamenti per metodo",
  subtitle,
  categoryId,
  vendorId,
  testId = "payment-method-breakdown",
  compact = false,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    setLoading(true);
    const params = { date_from: dateFrom, date_to: dateTo };
    if (categoryId) params.category_id = categoryId;
    if (vendorId) params.vendor_id = vendorId;
    paymentsBreakdown(params)
      .then((res) => setData(res))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [dateFrom, dateTo, categoryId, vendorId]);

  const rows = useMemo(() => data?.by_method || [], [data]);
  const total = num(data?.total_amount);

  return (
    <div
      data-testid={testId}
      className={
        "rounded-xl border border-stone-200 bg-white " +
        (compact ? "p-4" : "p-5")
      }
    >
      <div className="mb-3 flex items-baseline justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-600">
            {title}
          </h2>
          {subtitle && (
            <p className="mt-0.5 text-[11px] text-stone-500">{subtitle}</p>
          )}
        </div>
        <span
          data-testid={`${testId}-total`}
          className="font-mono text-sm font-semibold tabular-nums text-stone-900"
        >
          {EUR(total)}
        </span>
      </div>

      {loading && (
        <p className="py-6 text-center text-xs text-stone-500">Caricamento…</p>
      )}
      {!loading && rows.length === 0 && (
        <p className="py-6 text-center text-xs text-stone-500">
          Nessun pagamento nel periodo selezionato.
        </p>
      )}
      {!loading && rows.length > 0 && (
        <ul className="space-y-2.5">
          {rows.map((r) => {
            const pct = num(r.share_pct) * 100;
            return (
              <li
                key={r.method}
                data-testid={`${testId}-row-${r.method}`}
                className="space-y-1"
              >
                <div className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="flex items-baseline gap-2">
                    <span className="font-medium text-stone-800">
                      {paymentMethodIT[r.method] || r.method}
                    </span>
                    <span className="text-[11px] text-stone-400">
                      ({r.count})
                    </span>
                  </span>
                  <span className="flex items-baseline gap-3">
                    <span className="text-[11px] text-stone-500">
                      {pct.toFixed(1)}%
                    </span>
                    <span className="font-mono font-semibold tabular-nums text-stone-900">
                      {EUR(r.amount)}
                    </span>
                  </span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-stone-100">
                  <div
                    className={
                      "h-full rounded-full " +
                      (r.method === "cash"
                        ? "bg-emerald-500"
                        : r.method === "bank_transfer"
                        ? "bg-blue-500"
                        : r.method === "card"
                        ? "bg-stone-600"
                        : r.method === "unspecified"
                        ? "bg-stone-300"
                        : "bg-amber-500")
                    }
                    style={{ width: `${Math.min(100, Math.max(2, pct))}%` }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
